import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Tabs,
  Tab,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button
} from '@mui/material';
import {
  Dns as DnsIcon,
  Visibility as VisibilityIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  PolylineOutlined as PolylineOutlinedIcon,
  IntegrationInstructionsOutlined as IntegrationInstructionsOutlinedIcon,
  DnsOutlined as DnsOutlinedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { serverManagementLogger } from '../../utils/logger';
import { getAuthHeaders  } from '../../utils/authUtils';
import { apiFetch, getApiBaseUrl } from '../../utils/apiConfig';

// Import subcomponents
import ViewerSettings from './serverManagement/ViewerSettings';
import PacsDatabase from './serverManagement/PacsDatabase';
import DicomSendNodes from './DicomSendNodes';
import Hl7Settings from './serverManagement/Hl7Settings';
import AdminConfig from './serverManagement/AdminConfig';
import Hl7MappingDialogs from './serverManagement/Hl7MappingDialogs';
import Hl7HelpDialog from './serverManagement/Hl7HelpDialog';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`server-tabpanel-${index}`}
      aria-labelledby={`server-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const ServerManagement = ({ initialTab = 0, hideHeader = false }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);

  // Viewer Settings
  const [viewerSettings, setViewerSettings] = useState({
    web: { base_url: '', test_study_uid: '' },
    client: { base_url: '', test_study_uid: '' }
  });

  // PACS Database Settings
  const [pacsDbSettings, setPacsDbSettings] = useState({
    host: '',
    port: '',
    database: '',
    username: '',
    password: ''
  });

  // Test Results
  const [testResults, setTestResults] = useState({});

  // Admin Config states
  const [adminConfig, setAdminConfig] = useState({
    use_approval_workflow: false,
    jwt_secret: '',
    token_expiry: '30'
  });

  // HL7 Mapping states
  const [hl7Mappings, setHl7Mappings] = useState([]);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [hl7TestDialogOpen, setHl7TestDialogOpen] = useState(false);
  const [hl7TestMapping, setHl7TestMapping] = useState(null);
  const [hl7TestMessage, setHl7TestMessage] = useState('');
  const [hl7TestResult, setHl7TestResult] = useState(null);
  const [inlineTestResult, setInlineTestResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(new Set());
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [hl7AccordionExpanded, setHl7AccordionExpanded] = useState('ris');
  const [confirmDeleteMapping, setConfirmDeleteMapping] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null
  });
  
  // HL7 Mapping form states
  const [mappingForm, setMappingForm] = useState({
    name: '',
    description: '',
    source_system: 'ris',
    is_active: true,
    mapping_rules: {
      patient_id: { segment: 'PID', field: 3, subfield: 1, counter: '' },
      patient_name: { segment: 'PID', field: 5, subfield: 0, format: 'first_last', counter: '' },
      birth_date: { segment: 'PID', field: 7, subfield: 0, format: 'date', counter: '' },
      sex: { segment: 'PID', field: 8, subfield: 0, counter: '' },
      modality: { segment: 'OBR', field: 4, subfield: 0, counter: '' },
      study_description: { segment: 'OBR', field: 4, subfield: 1, counter: '' },
      clinical_info: { segment: 'NTE', field: 3, subfield: 0, counter: 1 },
      question: { segment: 'NTE', field: 3, subfield: 0, counter: 2 },
      accession_number: { segment: 'ORC', field: 2, subfield: 0, counter: '' },
      referring_physician: { segment: 'PV1', field: 6, subfield: '2,3,4,5', counter: '' },
      admission_id: { segment: 'PV1', field: 19, subfield: 0, counter: '' }
    },
    sample_message: ''
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    serverManagementLogger.info('Server management tab changed', { tab_index: newValue });
  };

  const handleHl7AccordionChange = (panel) => (event, isExpanded) => {
    setHl7AccordionExpanded(isExpanded ? panel : false);
  };

  // Load all settings
  const loadSettings = useCallback(async () => {
    serverManagementLogger.info('Loading server management settings');
    try {
      const authHeaders = getAuthHeaders();
      
      // Load viewer settings
      const viewerResponse = await apiFetch('/viewer-settings/');
      if (viewerResponse.ok) {
        const viewerData = await viewerResponse.json();
        const settings = { 
          web: { 
            base_url: 'http://localhost/jivexmobile/?user=guest&password=Guest&mode=load&studyInstanceUID=[studyInstanceUID]',
            test_study_uid: ''
          }, 
          client: { 
            base_url: 'jivexrv:rv.startmode=load&rv.load.data.type=study&rv.load.data=[studyInstanceUID]',
            test_study_uid: ''
          } 
        };
        viewerData.forEach(setting => {
          settings[setting.viewer_type] = {
            base_url: setting.base_url || settings[setting.viewer_type].base_url,
            test_study_uid: setting.test_study_uid || ''
          };
        });
        setViewerSettings(settings);
        serverManagementLogger.info('Viewer settings loaded successfully', { viewer_types: Object.keys(settings) });
      }

      // Load PACS settings
      const pacsConfigResponse = await apiFetch('/admin/pacs-config');
      if (pacsConfigResponse.ok) {
        const pacsConfigData = await pacsConfigResponse.json();
        setPacsDbSettings({
          host: pacsConfigData.host || '',
          port: pacsConfigData.port || '',
          database: pacsConfigData.database || '',
          username: pacsConfigData.username || '',
          password: pacsConfigData.password || '',
          connection_timeout: pacsConfigData.connection_timeout || '30',
          query_timeout: pacsConfigData.query_timeout || '60'
        });
        
        serverManagementLogger.info('PACS settings loaded successfully');
      }

      // Load admin configuration separately
      const configResponse = await apiFetch('/admin/config');
      if (configResponse.ok) {
        const configData = await configResponse.json();
        setAdminConfig({
          use_approval_workflow: configData.Server?.use_approval_workflow === 'true',
          jwt_secret: configData.Security?.jwt_secret || '',
          token_expiry: configData.Security?.token_expiry_days || '30'
        });
        
        serverManagementLogger.info('Admin config loaded successfully');
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('admin.serverManagement.loadError') });
      serverManagementLogger.error('Failed to load server management settings', { error: error.message });
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Sync activeTab mit initialTab prop
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Test Viewer
  const testViewer = async (viewerType) => {
    setLoading(true);
    serverManagementLogger.info('Testing viewer', { viewer_type: viewerType });
    try {
      const settings = viewerSettings[viewerType];
      
      if (!settings.base_url) {
        setTestResults(prev => ({ ...prev, [viewerType]: { success: false, message: t('admin.serverManagement.noUrl') } }));
        serverManagementLogger.warn('Viewer test failed: no URL configured', { viewer_type: viewerType });
        return;
      }

      const testUrl = settings.base_url.replace('[studyInstanceUID]', settings.test_study_uid || 'test');
      window.open(testUrl, '_blank');
      
      setTestResults(prev => ({ ...prev, [viewerType]: { success: true, message: t('admin.serverManagement.viewerOpened') } }));
      serverManagementLogger.info('Viewer test successful', { viewer_type: viewerType, test_url: testUrl });
    } catch (error) {
      setTestResults(prev => ({ ...prev, [viewerType]: { success: false, message: error.message } }));
      serverManagementLogger.error('Viewer test failed', { viewer_type: viewerType, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Test PACS Database
  const testPacsDatabase = async () => {
    setLoading(true);
    serverManagementLogger.info('Testing PACS database connection', { host: pacsDbSettings.host, database: pacsDbSettings.database });
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/admin/test-pacs-connection`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pacsDbSettings)
      });
      
      const result = await response.json();
      setTestResults(prev => ({ 
        ...prev, 
        pacsDb: {
          success: result.success,
          message: result.success ? t('admin.serverManagement.pacsConnectionSuccess') : t('admin.serverManagement.pacsConnectionError')
        }
      }));
      
      if (result.success) {
        serverManagementLogger.info('PACS database test successful', { host: pacsDbSettings.host });
      } else {
        serverManagementLogger.error('PACS database test failed', { host: pacsDbSettings.host, error: result.message });
      }
    } catch (error) {
      setTestResults(prev => ({ 
        ...prev, 
        pacsDb: { 
          success: false, 
          message: t('admin.serverManagement.pacsConnectionError')
        } 
      }));
      serverManagementLogger.error('PACS database test failed', { host: pacsDbSettings.host, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Save functions
  const saveViewerSettings = async (viewerType) => {
    setLoading(true);
    serverManagementLogger.info('Saving viewer settings', { viewer_type: viewerType });
    try {
      const authHeaders = getAuthHeaders();
      const settings = viewerSettings[viewerType];
      
      // Zuerst prüfen, ob bereits eine Einstellung für diesen viewer_type existiert
      const checkResponse = await fetch(`${getApiBaseUrl()}/viewer-settings/`, {
        method: 'GET',
        headers: authHeaders
      });
      
      let existingSettingId = null;
      if (checkResponse.ok) {
        const existingSettings = await checkResponse.json();
        const existingSetting = existingSettings.find(s => s.viewer_type === viewerType);
        if (existingSetting) {
          existingSettingId = existingSetting.id;
        }
      }
      
      // Wenn Einstellung existiert, PUT verwenden, sonst POST
      const method = existingSettingId ? 'PUT' : 'POST';
      const url = existingSettingId 
        ? `${getApiBaseUrl()}/viewer-settings/${existingSettingId}` 
        : `${getApiBaseUrl()}/viewer-settings/`;
      
      const response = await fetch(url, {
        method: method,
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          viewer_type: viewerType,
          base_url: settings.base_url,
          test_study_uid: settings.test_study_uid
        })
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: t('admin.serverManagement.saveSuccess') });
        serverManagementLogger.info('Viewer settings saved successfully', { 
          viewer_type: viewerType, 
          method: method,
          setting_id: existingSettingId 
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.detail || t('admin.serverManagement.saveError') });
        serverManagementLogger.error('Failed to save viewer settings', { 
          viewer_type: viewerType, 
          status: response.status,
          error: errorData.detail 
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('admin.serverManagement.saveError') });
      serverManagementLogger.error('Failed to save viewer settings', { viewer_type: viewerType, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const savePacsSettings = async () => {
    setLoading(true);
    serverManagementLogger.info('Saving PACS settings', { host: pacsDbSettings.host, database: pacsDbSettings.database });
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/admin/save-pacs-settings`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pacsDbSettings)
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: t('admin.serverManagement.saveSuccess') });
        serverManagementLogger.info('PACS settings saved successfully', { host: pacsDbSettings.host });
      } else {
        setMessage({ type: 'error', text: t('admin.serverManagement.saveError') });
        serverManagementLogger.error('Failed to save PACS settings', { host: pacsDbSettings.host, status: response.status });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('admin.serverManagement.saveError') });
      serverManagementLogger.error('Failed to save PACS settings', { host: pacsDbSettings.host, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Save Admin Config
  const saveAdminConfig = async () => {
    setLoading(true);
    serverManagementLogger.info('Saving admin configuration');
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/admin/save-config`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(adminConfig)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('admin.serverManagement.saveSuccess') });
        serverManagementLogger.info('Admin configuration saved successfully');
      } else {
        setMessage({ type: 'error', text: t('admin.serverManagement.saveError') });
        serverManagementLogger.error('Failed to save admin configuration');
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('admin.serverManagement.saveError') });
      serverManagementLogger.error('Failed to save admin configuration', { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Load HL7 mappings
  const loadHl7Mappings = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await apiFetch('/admin/hl7-mappings');
      if (response.ok) {
        const data = await response.json();
        setHl7Mappings(data);
      }
    } catch (err) {
      console.error('Error loading HL7 mappings:', err);
    }
  };

  useEffect(() => {
    loadHl7Mappings();
  }, []);

  // HL7 Mapping handlers
  const handleMappingInputChange = (field, value) => {
    setMappingForm(prev => ({ ...prev, [field]: value }));
  };

  const handleMappingRuleChange = (field, value) => {
    setMappingForm(prev => ({
      ...prev,
      mapping_rules: { ...prev.mapping_rules, [field]: value }
    }));
  };

  const resetMappingForm = () => {
    setMappingForm({
      name: '',
      description: '',
      source_system: 'ris',
      is_active: true,
      mapping_rules: {
        patient_id: { segment: 'PID', field: 3, subfield: '1', counter: '' },
        patient_name: { segment: 'PID', field: 5, subfield: '', format: 'first_last', counter: '' },
        birth_date: { segment: 'PID', field: 7, subfield: '0', format: 'date', counter: '' },
        sex: { segment: 'PID', field: 8, subfield: '0', counter: '' },
        modality: { segment: 'OBR', field: 4, subfield: '', counter: '' },
        study_description: { segment: 'OBR', field: 4, subfield: '1', counter: '' },
        clinical_info: { segment: 'NTE', field: 3, subfield: '0', counter: 1, multiple: true },
        question: { segment: 'NTE', field: 3, subfield: 0, counter: 2 },
        accession_number: { segment: 'ORC', field: 2, subfield: 0, counter: '' },
        referring_physician: { segment: 'PV1', field: 6, subfield: '2,3,4,5', counter: '' },
        admission_id: { segment: 'PV1', field: 19, subfield: 0, counter: '' },
        tenant: { segment: '', field: '', subfield: '', format: '', multiple: false, counter: '' }
      },
      sample_message: ''
    });
  };

  const openMappingDialog = (mapping = null, sourceSystem = 'ris') => {
    if (mapping) {
      setEditingMapping(mapping);
      
      // Definiere alle erforderlichen Felder
      const requiredFields = [
        'patient_id', 'patient_name', 'birth_date', 'sex', 'modality', 
        'study_description', 'clinical_info', 'question', 'accession_number',
        'referring_physician', 'admission_id', 'tenant'
      ];
      
      const normalizedRules = {};
      
      // Füge existierende Regeln hinzu
      Object.entries(mapping.mapping_rules || {}).forEach(([fieldName, rule]) => {
        normalizedRules[fieldName] = {
          ...rule,
          counter: rule.counter || ''
        };
      });
      
      // Füge fehlende Felder mit Standard-Werten hinzu
      requiredFields.forEach(fieldName => {
        if (!normalizedRules[fieldName]) {
          normalizedRules[fieldName] = {
            segment: '',
            field: '',
            subfield: '',
            format: '',
            multiple: false,
            counter: ''
          };
        }
      });
      
      setMappingForm({
        name: mapping.name,
        description: mapping.description || '',
        source_system: mapping.source_system,
        is_active: mapping.is_active,
        mapping_rules: normalizedRules,
        sample_message: mapping.sample_message || ''
      });
    } else {
      setEditingMapping(null);
      resetMappingForm();
      setMappingForm(prev => ({ ...prev, source_system: sourceSystem }));
    }
    setMappingDialogOpen(true);
  };

  const closeMappingDialog = () => {
    setMappingDialogOpen(false);
    setEditingMapping(null);
    resetMappingForm();
    setInlineTestResult(null);
    setFieldErrors(new Set());
  };

  const testCurrentMapping = async () => {
    if (!mappingForm.sample_message.trim()) {
      setMessage({ type: 'error', text: t('admin.hl7Mapping.noSampleMessage') });
      return;
    }

    setLoading(true);
    try {
      const authHeaders = getAuthHeaders();
      
      const cleanedRules = {};
      Object.entries(mappingForm.mapping_rules).forEach(([fieldName, rule]) => {
        cleanedRules[fieldName] = {
          segment: rule.segment || '',
          field: rule.field || 1,
          subfield: rule.subfield === '' || rule.subfield === null ? null : rule.subfield,
          counter: rule.counter === '' || rule.counter === null ? null : parseInt(rule.counter) || null,
          format: rule.format || null,
          multiple: rule.multiple || false
        };
      });

      const response = await fetch(`${getApiBaseUrl()}/admin/hl7-mappings/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          hl7_message: mappingForm.sample_message,
          mapping_rules: cleanedRules
        })
      });

      const responseText = await response.text();
      console.log('Raw response text:', responseText);
      
      if (response.ok) {
        // Versuche die Daten als JSON zu parsen
        let parsedData;
        try {
          parsedData = JSON.parse(responseText);
        } catch (e) {
          console.error('Failed to parse response:', e);
          throw new Error('Invalid response format');
        }

        console.log('Parsed test results:', parsedData);

        // Extrahiere die relevanten Informationen
        const transformedData = {
          success: true,
          errors: [],
          parsed_fields: {},
          detailed_results: {}
        };

        // Verarbeite die Ergebnisse direkt aus detailed_results
        if (parsedData.detailed_results) {
          transformedData.detailed_results = parsedData.detailed_results;
          
          Object.entries(parsedData.detailed_results).forEach(([fieldName, result]) => {
            // Ein Feld ist nur erfolgreich wenn status 'success' ist UND es einen gültigen final_value hat
            if (result.status === 'success' && result.final_value && result.final_value !== 'N/A') {
              transformedData.parsed_fields[fieldName] = result.final_value;
            } else {
              transformedData.errors.push(fieldName);
            }
          });
        }

        // Setze den Gesamtstatus
        transformedData.success = transformedData.errors.length === 0;

        console.log('Transformed test results:', transformedData);
        setInlineTestResult(transformedData);
        setFieldErrors(new Set(transformedData.errors));
      } else {
        let errorMessage = 'Test failed';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || t('admin.hl7Mapping.testFailed');
        } catch (e) {
          errorMessage = t('admin.hl7Mapping.testFailed');
        }
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      console.error('Test error:', error);
      setMessage({ type: 'error', text: t('admin.hl7Mapping.testError') });
    } finally {
      setLoading(false);
    }
  };

  const saveMapping = async () => {
    setLoading(true);
    try {
      const authHeaders = getAuthHeaders();
      const url = editingMapping 
        ? `${getApiBaseUrl()}/admin/hl7-mappings/${editingMapping.id}`
        : `${getApiBaseUrl()}/admin/hl7-mappings`;
      
      const cleanedMappingForm = {
        ...mappingForm,
        mapping_rules: {}
      };
      
      Object.entries(mappingForm.mapping_rules).forEach(([fieldName, rule]) => {
        cleanedMappingForm.mapping_rules[fieldName] = {
          segment: rule.segment || '',
          field: rule.field || 1,
          subfield: rule.subfield || 0,
          counter: rule.counter === '' || rule.counter === null || rule.counter === undefined ? null : parseInt(rule.counter) || null,
          format: rule.format || null,
          multiple: rule.multiple || false
        };
      });
      
      const response = await fetch(url, {
        method: editingMapping ? 'PUT' : 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanedMappingForm)
      });

      if (response.ok) {
        const successMessage = editingMapping 
          ? t('admin.hl7Mapping.updateSuccess')
          : t('admin.hl7Mapping.createSuccess');
        setMessage({ type: 'success', text: successMessage });
        closeMappingDialog();
        loadHl7Mappings();
      } else {
        setMessage({ type: 'error', text: t('admin.hl7Mapping.saveError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('admin.hl7Mapping.saveError') });
    } finally {
      setLoading(false);
    }
  };

  const deleteMapping = async (mappingId) => {
    const mapping = hl7Mappings.find(m => m.id === mappingId);
    const mappingName = mapping?.name || 'Unknown';
    
    setConfirmDeleteMapping({
      open: true,
      title: t('admin.hl7Mapping.confirmations.deleteMappingTitle'),
      description: t('admin.hl7Mapping.confirmations.deleteMappingDescription', { name: mappingName }),
      onConfirm: async () => {
        setLoading(true);
        try {
          const authHeaders = getAuthHeaders();
          const response = await fetch(`${getApiBaseUrl()}/admin/hl7-mappings/${mappingId}`, {
            method: 'DELETE',
            headers: authHeaders
          });

          if (response.ok) {
            setMessage({ type: 'success', text: t('admin.serverManagement.saveSuccess') });
            loadHl7Mappings();
          } else {
            setMessage({ type: 'error', text: t('admin.serverManagement.saveError') });
          }
        } catch (err) {
          setMessage({ type: 'error', text: t('admin.serverManagement.saveError') });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const closeTestDialog = () => {
    setHl7TestDialogOpen(false);
    setHl7TestMapping(null);
    setHl7TestMessage('');
    setHl7TestResult(null);
  };

  const testMapping = async () => {
    if (!hl7TestMessage.trim()) return;
    
    setLoading(true);
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/admin/hl7-mappings/test`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mapping_rules: hl7TestMapping.mapping_rules,
          hl7_message: hl7TestMessage
        })
      });

      if (response.ok) {
        const result = await response.json();
        setHl7TestResult(result);
      } else {
        setHl7TestResult({ success: false, error: 'Fehler beim Testen des Mappings' });
      }
    } catch (err) {
      setHl7TestResult({ success: false, error: 'Fehler beim Testen des Mappings' });
    } finally {
      setLoading(false);
    }
  };

  // Wenn in Admin-Sidebar eingebettet, nur Content rendern
  if (hideHeader) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {message.text && (
          <Alert severity={message.type} sx={{ m: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}
        
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <TabPanel value={activeTab} index={0}>
            <ViewerSettings
              viewerSettings={viewerSettings}
              setViewerSettings={setViewerSettings}
              testResults={testResults}
              loading={loading}
              testViewer={testViewer}
              saveViewerSettings={saveViewerSettings}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <PacsDatabase
              pacsDbSettings={pacsDbSettings}
              setPacsDbSettings={setPacsDbSettings}
              testResults={testResults}
              loading={loading}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              testPacsDatabase={testPacsDatabase}
              savePacsSettings={savePacsSettings}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <DicomSendNodes />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Hl7Settings
              hl7AccordionExpanded={hl7AccordionExpanded}
              handleHl7AccordionChange={handleHl7AccordionChange}
              hl7Mappings={hl7Mappings}
              openMappingDialog={openMappingDialog}
              deleteMapping={deleteMapping}
              loadHl7Mappings={loadHl7Mappings}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <AdminConfig 
              adminConfig={adminConfig}
              setAdminConfig={setAdminConfig}
              loading={loading}
              saveAdminConfig={saveAdminConfig}
            />
          </TabPanel>
        </Box>

        <Hl7MappingDialogs
          mappingDialogOpen={mappingDialogOpen}
          closeMappingDialog={closeMappingDialog}
          editingMapping={editingMapping}
          mappingForm={mappingForm}
          handleMappingInputChange={handleMappingInputChange}
          handleMappingRuleChange={handleMappingRuleChange}
          testCurrentMapping={testCurrentMapping}
          saveMapping={saveMapping}
          loading={loading}
          inlineTestResult={inlineTestResult}
          fieldErrors={fieldErrors}
          setFieldErrors={setFieldErrors}
          setHelpDialogOpen={setHelpDialogOpen}
          helpDialogOpen={helpDialogOpen}
          hl7TestDialogOpen={hl7TestDialogOpen}
          closeTestDialog={closeTestDialog}
          hl7TestMapping={hl7TestMapping}
          hl7TestMessage={hl7TestMessage}
          setHl7TestMessage={setHl7TestMessage}
          testMapping={testMapping}
          hl7TestResult={hl7TestResult}
        />

        <Hl7HelpDialog
          helpDialogOpen={helpDialogOpen}
          setHelpDialogOpen={setHelpDialogOpen}
        />

        {/* Delete Mapping Confirmation Dialog */}
        <Dialog
          open={confirmDeleteMapping.open}
          onClose={() => setConfirmDeleteMapping({ ...confirmDeleteMapping, open: false })}
        >
          <DialogTitle>{confirmDeleteMapping.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmDeleteMapping.description}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteMapping({ ...confirmDeleteMapping, open: false })} className="button-cancel">
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={() => {
                if (confirmDeleteMapping.onConfirm) confirmDeleteMapping.onConfirm();
                setConfirmDeleteMapping({ ...confirmDeleteMapping, open: false });
              }} 
              color="primary" 
              autoFocus
              className="button-delete"
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DnsIcon color="primary" />
        {t('admin.tabs.serverManagement')}
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ 
        width: '100%', 
        mt: 3, 
        height: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="server management tabs"
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<VisibilityIcon />} 
            label={t('admin.serverManagement.viewerSettings')} 
            id="server-tab-0"
            aria-controls="server-tabpanel-0"
          />
          <Tab 
            icon={<Inventory2OutlinedIcon />} 
            label={t('admin.serverManagement.pacsSettings')} 
            id="server-tab-1"
            aria-controls="server-tabpanel-1"
          />
          <Tab 
            icon={<PolylineOutlinedIcon />} 
            label={t('admin.serverManagement.dicomSettings')} 
            id="server-tab-2"
            aria-controls="server-tabpanel-2"
          />
          <Tab 
            icon={<IntegrationInstructionsOutlinedIcon />} 
            label={t('admin.serverManagement.hl7Settings')} 
            id="server-tab-3"
            aria-controls="server-tabpanel-3"
          />
          <Tab 
            icon={<DnsOutlinedIcon />} 
            label={t('admin.serverManagement.adminConfig')} 
            id="server-tab-4"
            aria-controls="server-tabpanel-4"
          />
        </Tabs>

        <Box sx={{ 
          overflow: 'auto', 
          flex: 1,
          height: 0  // Force flex calculation
        }}>
          <TabPanel value={activeTab} index={0}>
            <ViewerSettings
              viewerSettings={viewerSettings}
              setViewerSettings={setViewerSettings}
              testResults={testResults}
              loading={loading}
              testViewer={testViewer}
              saveViewerSettings={saveViewerSettings}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <PacsDatabase
              pacsDbSettings={pacsDbSettings}
              setPacsDbSettings={setPacsDbSettings}
              testResults={testResults}
              loading={loading}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              testPacsDatabase={testPacsDatabase}
              savePacsSettings={savePacsSettings}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <DicomSendNodes />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Hl7Settings
              hl7AccordionExpanded={hl7AccordionExpanded}
              handleHl7AccordionChange={handleHl7AccordionChange}
              hl7Mappings={hl7Mappings}
              openMappingDialog={openMappingDialog}
              deleteMapping={deleteMapping}
              loadHl7Mappings={loadHl7Mappings}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <AdminConfig 
              adminConfig={adminConfig}
              setAdminConfig={setAdminConfig}
              loading={loading}
              saveAdminConfig={saveAdminConfig}
            />
          </TabPanel>
        </Box>
      </Paper>

      <Hl7MappingDialogs
        mappingDialogOpen={mappingDialogOpen}
        closeMappingDialog={closeMappingDialog}
        editingMapping={editingMapping}
        mappingForm={mappingForm}
        handleMappingInputChange={handleMappingInputChange}
        handleMappingRuleChange={handleMappingRuleChange}
        testCurrentMapping={testCurrentMapping}
        saveMapping={saveMapping}
        loading={loading}
        inlineTestResult={inlineTestResult}
        fieldErrors={fieldErrors}
        setFieldErrors={setFieldErrors}
        setHelpDialogOpen={setHelpDialogOpen}
        helpDialogOpen={helpDialogOpen}
        hl7TestDialogOpen={hl7TestDialogOpen}
        closeTestDialog={closeTestDialog}
        hl7TestMapping={hl7TestMapping}
        hl7TestMessage={hl7TestMessage}
        setHl7TestMessage={setHl7TestMessage}
        testMapping={testMapping}
        hl7TestResult={hl7TestResult}
      />

      <Hl7HelpDialog
        helpDialogOpen={helpDialogOpen}
        setHelpDialogOpen={setHelpDialogOpen}
      />

      {/* Delete Mapping Confirmation Dialog */}
      <Dialog
        open={confirmDeleteMapping.open}
        onClose={() => setConfirmDeleteMapping({ ...confirmDeleteMapping, open: false })}
      >
        <DialogTitle>{confirmDeleteMapping.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDeleteMapping.description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteMapping({ ...confirmDeleteMapping, open: false })} className="button-cancel">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => {
              if (confirmDeleteMapping.onConfirm) confirmDeleteMapping.onConfirm();
              setConfirmDeleteMapping({ ...confirmDeleteMapping, open: false });
            }} 
            color="primary" 
            autoFocus
            className="button-delete"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServerManagement; 